import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { Label } from "@/components/ui/Input";
import {
  DEFAULT_MRP_TRANSFER_REASON,
  fetchMrpTransferReasonCatalog,
  mrpTransferReasonLabel,
  normalizeMrpTransferReason,
  type MrpTransferReason,
} from "@/lib/mrp";
import { cn } from "@/lib/utils";

const TRANSFER_REASON_CATALOG_QUERY_KEY = ["mrp", "transfer-reasons"] as const;

export function useTransferReasonCatalog() {
  return useQuery({
    queryKey: TRANSFER_REASON_CATALOG_QUERY_KEY,
    queryFn: fetchMrpTransferReasonCatalog,
    staleTime: 5 * 60_000,
  });
}

interface TransferReasonSelectProps {
  id?: string;
  label?: string;
  value: MrpTransferReason | string;
  onChange: (code: MrpTransferReason) => void;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function TransferReasonSelect({
  id,
  label,
  value,
  onChange,
  disabled = false,
  className,
  triggerClassName,
}: TransferReasonSelectProps): JSX.Element {
  const catalogQuery = useTransferReasonCatalog();
  const normalizedValue = normalizeMrpTransferReason(value);
  const [searchValue, setSearchValue] = useState("");

  const options: SearchableSelectOption[] = useMemo(() => {
    const rows = catalogQuery.data ?? [];
    if (rows.length === 0) {
      return [
        {
          value: normalizedValue,
          label: mrpTransferReasonLabel(normalizedValue),
        },
      ];
    }
    return rows.map((row) => ({
      value: row.code,
      label: row.label,
      description: row.description,
    }));
  }, [catalogQuery.data, normalizedValue]);

  const control = (
    <SearchableSelect
      id={id}
      value={normalizedValue}
      onValueChange={(option) => {
        if (option != null) onChange(normalizeMrpTransferReason(option.value));
      }}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      disabled={disabled || catalogQuery.isLoading}
      placeholder="Select reason…"
      searchPlaceholder="Search reasons…"
      emptyMessage="No matching reasons."
      isLoading={catalogQuery.isLoading}
      triggerClassName={cn("h-10 w-full", triggerClassName)}
    />
  );

  if (label == null || label.trim() === "") {
    return <div className={className}>{control}</div>;
  }

  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      <div className="mt-1">{control}</div>
    </div>
  );
}

export function transferReasonDefault(): MrpTransferReason {
  return DEFAULT_MRP_TRANSFER_REASON;
}
